import * as mix from "./mix.js";
import { mediaUrl } from "./media.js";

/**
 * Crowd atmosphere.
 *
 * A bed of crowd noise loops quietly underneath everything, and lifts on the
 * moments that deserve it — a boundary, a wicket, the end of a chase. It is the
 * difference between a scoring app and something that sounds like a match is
 * happening.
 *
 * Two rules it follows:
 *
 * · The bed is quiet. Loud enough to notice when it stops, never loud enough to
 *   compete with the commentary. It ducks further while a line is being spoken.
 *
 * · Reactions are short and one at a time. Overlapping cheers turn into mush,
 *   and a cheer arriving three balls late is worse than none.
 *
 * The clips are the ones the first version of this app shipped with, recovered
 * from the old branch — real crowd recordings at 48 kHz, which no amount of
 * synthesis would improve on.
 */


export type Reaction = "four" | "six" | "wicket" | "win" | "appeal" | "near";

/** Which recording, and how loud, for each moment. */
const REACTION: Record<Reaction, { file: string; gain: number }> = {
  four:   { file: "cheer-small", gain: 0.55 },
  six:    { file: "cheer-big",   gain: 0.8 },
  wicket: { file: "roar",        gain: 0.85 },
  win:    { file: "roar",        gain: 0.95 },
  appeal: { file: "cheer-small", gain: 0.45 },
  near:   { file: "clap",        gain: 0.35 },
};

/**
 * The bed, at the top of its fader.
 *
 * Was 0.11, and then multiplied by the crowd fader on top — so the shipped
 * default came out around 0.06 of an already quiet file. Two attenuations for
 * one decision. The fader is now the control and this is what full means; the
 * "present, never intrusive" balance lives in the fader's default instead,
 * where somebody can actually move it.
 */
const BED_VOLUME = 0.55;        // at 100% on the fader
const BED_DUCKED = 0.05;        // while a commentary line is speaking

let bed: HTMLAudioElement | null = null;
let reaction: HTMLAudioElement | null = null;
let enabled = false;
let ducked = false;

export function isOn(): boolean { return enabled; }

/**
 * Start the atmosphere. Must be called from a user gesture — an audio element
 * will not play otherwise, and it fails silently.
 */
export function start(): void {
  if (enabled) return;
  if (!bed) {
    bed = new Audio();
    bed.loop = true;
    bed.preload = "auto";
  }
  bed.volume = BED_VOLUME * mix.get("crowd");
  // Via the media store so it is authenticated where it needs to be, and on the
  // device once heard.
  void mediaUrl(`/api/v1/commentary/v1/crowd/crowd-bed.mp3`).then((url) => {
    if (!url || !bed) return;
    bed.src = url;
    void bed.play().then(() => { enabled = true; }).catch(() => { enabled = false; });
  });
}

export function stop(): void {
  enabled = false;
  if (bed) { bed.pause(); bed.currentTime = 0; }
  if (reaction) { reaction.pause(); }
}

/** Pull the bed down while a line is spoken, and let it back up after. */
export function duck(on: boolean): void {
  ducked = on;
  if (bed && enabled) bed.volume = (on ? BED_DUCKED : BED_VOLUME) * mix.get("crowd");
}

/**
 * React to a moment.
 *
 * Fires and forgets. If a reaction is already playing it is replaced rather
 * than layered, because two cheers at once is noise rather than atmosphere.
 */
export function react(kind: Reaction): void {
  if (!enabled) return;
  const r = REACTION[kind];
  if (!reaction) reaction = new Audio();
  try {
    reaction.pause();
    reaction.volume = r.gain * mix.get("crowd");
    void mediaUrl(`/api/v1/commentary/v1/crowd/${r.file}.mp3`).then((url) => {
      if (!url || !reaction) return;
      reaction.src = url;
      void reaction.play().catch(() => undefined);
    });

    // Lift the bed briefly too, so the ground itself sounds louder rather than
    // just a cheer being played over it.
    if (bed) {
      const base = (ducked ? BED_DUCKED : BED_VOLUME) * mix.get("crowd");
      bed.volume = Math.min(1, base * 2.6);
      window.setTimeout(() => {
        if (bed && enabled) bed.volume = ducked ? BED_DUCKED : BED_VOLUME;
      }, 2600);
    }
  } catch { /* audio unavailable */ }
}

/** Work out what the crowd would do about a line of commentary. */
export function reactionFor(text: string, kind: string): Reaction | null {
  if (kind === "result") return "win";
  if (kind !== "ball") return null;
  if (/BOWLED|CAUGHT|LBW|RUN OUT|STUMPED|HIT WICKET|WICKET|Timber|Castled|Cleaned him/i.test(text)) {
    return "wicket";
  }
  if (/^SIX/.test(text)) return "six";
  if (/^FOUR/.test(text)) return "four";
  // A beaten edge or a close call gets a murmur rather than silence.
  if (/beaten|plays and misses|appeal/i.test(text)) return "near";
  return null;
}

/** Warm the clips so the first boundary is not a download. */
export function prime(): void {
  for (const name of ["crowd-bed", "cheer-small", "cheer-big", "roar", "clap"]) {
    void mediaUrl(`/api/v1/commentary/v1/crowd/${name}.mp3`);
  }
}

// Moving the crowd fader must take effect on the bed that is already playing,
// not only on the next one to start.
mix.onChange((m) => {
  if (bed && enabled) bed.volume = (ducked ? BED_DUCKED : BED_VOLUME) * m.crowd;
});
